'use client'

import { Bell, BellOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { usePushNotifications } from '@/hooks/use-push-notifications'
import { cn } from '@/lib/html/class-name'

interface NotificationSettingsProps {
  className?: string
}

export default function NotificationSettings({ className }: NotificationSettingsProps) {
  const {
    isSupported,
    isSubscribed,
    loading,
    subscribing,
    unsubscribing,
    getCurrentPermission,
    subscribe,
    unsubscribe,
  } = usePushNotifications()

  const permission = getCurrentPermission()

  if (!isSupported) {
    return (
      <Card className={cn(className, 'bg-background')}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="size-4" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Push notifications are not supported in this browser.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className={cn(className, 'gap-4 border-none shadow-none bg-background')}>
      <CardHeader className="p-0">
        <CardTitle className="flex items-center gap-2">
          <Bell className="size-4" />
          Push Notifications
        </CardTitle>
        <CardDescription className="text-placeholder text-sm leading-tight">
          Get notified about important updates and activities.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 p-0">
        <div className="flex items-center justify-between py-2">
          <div className="space-y-1">
            <Label className="text-sm font-medium">
              {isSubscribed ? 'Notifications Enabled' : 'Notifications Disabled'}
            </Label>
            <p className="text-sm text-placeholder leading-tight">
              {isSubscribed && 'You will receive push notifications.'}
              {!isSubscribed &&
                'Click to enable notifications. Ensure you have enabled notifications in your browser settings.'}
            </p>
          </div>

          {isSubscribed ? (
            <Button
              onClick={unsubscribe}
              variant="outline"
              size="sm"
              loading={unsubscribing}
              disabled={loading}
            >
              <BellOff className="size-3 mr-1.5" />
              Disable
            </Button>
          ) : (
            <Button
              onClick={subscribe}
              variant="primary"
              size="sm"
              loading={subscribing}
              disabled={loading || permission === 'denied'}
            >
              <Bell className="size-3 mr-1.5" />
              Enable
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
